import { makeAutoObservable } from 'mobx';

import { UserDetailsViewModel } from '../../Purchase/Cart/PersonalizedCart/UserDetailsViewModel.csharp';
import { getFor } from '../../ReactComponents/API';
import { LocalStorageAccessor } from '../../StorageAccessor/LocalStorageAccessor';
import { FavouritesViewModel } from './FavouritesViewModel.csharp';

interface IStorageFavouritesViewModel {
    model: FavouritesViewModel;
    expirationDate: number;
}

class Favourites {
    private _favouritesExpirationTimeInHours = 1;
    private _favouritesStorage = 'favourites';

    public items: FavouritesViewModel | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    public setFavourites = (favourites: FavouritesViewModel) => {
        this.items = favourites;
        this.saveFavouritesInLocalStorage(favourites);
    };

    public isFavourite = (code: string, initialValue = false): boolean => {
        if (this.items) {
            return this.items.favouriteProductCodes.includes(code);
        }

        return initialValue;
    };

    public validateFavourites = (favouritesUrl: string, user: UserDetailsViewModel) => {
        if (!user.isLoggedIn) {
            this.clearFavourites();
            return;
        }

        const data = this.getFavouritesFromLocalStorage();

        if (data === null || data.expirationDate < new Date().getTime() || data.model?.userId !== user.customerId) {
            this.updateFavourites(favouritesUrl);
        } else {
            this.setFavourites(data.model);
        }
    };

    private saveFavouritesInLocalStorage = (favourites: FavouritesViewModel) => {
        LocalStorageAccessor.setItem(
            this._favouritesStorage,
            JSON.stringify({
                model: favourites,
                expirationDate: this.getExpirationDate()
            })
        );
    };

    private getFavouritesFromLocalStorage = (): IStorageFavouritesViewModel | null => {
        const favouritesString = LocalStorageAccessor.getItem(this._favouritesStorage);
        return favouritesString ? (JSON.parse(favouritesString) as IStorageFavouritesViewModel) : null;
    };

    private clearFavourites = () => {
        LocalStorageAccessor.removeItem(this._favouritesStorage);
        this.items = this.buildEmptyFavourites();
    };

    private updateFavourites = (favouritesUrl: string) => {
        getFor<FavouritesViewModel>(`${favouritesUrl}`).then((favourites) => {
            this.setFavourites(favourites);
        });
    };

    private getExpirationDate = () => {
        const now = new Date();
        const hours = now.getHours() + this._favouritesExpirationTimeInHours;
        now.setHours(hours);
        return now.getTime();
    };

    private buildEmptyFavourites = (): FavouritesViewModel => {
        return { favouriteProductCodes: [] };
    };
}

export const favourites = new Favourites();
